// ════════════════════════════════════════════════════════════════════════════
//  StudentAlertsTab.jsx  ·  Centro de Alertas del Alumno
//  Location: src/components/dashboard/alerts/StudentAlertsTab.jsx
//
//  FIXES APLICADOS:
//    🔧 FIX 1 — readLocal y dismissed ahora se persisten en localStorage
//              → el estado de lectura/descarte sobrevive recargas de página
//    🔧 FIX 2 — KpiStrip recibe el conteo local (localUnread) en lugar de
//              data?.unread_count del servidor → los tiles se actualizan
//              inmediatamente al marcar como leída
// ════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback, useRef } from 'react';
import './StudentAlertsTab.css';
import { useLatido } from '../../../hooks/useLatido';
//  El interruptor de los avisos al telefono. Aqui y no en Configuracion:
//  esta es la pantalla donde el alumno lee lo que le pasa, y esto decide
//  si eso le llega al telefono o espera a que entre. Un interruptor
//  escondido en otro menu no se encuentra ni para apagarlo.
import AvisosDelAlumno from './AvisosDelAlumno';

// ─────────────────────────────────────────────────────────────────────────────
//  CONSTANTES
// ─────────────────────────────────────────────────────────────────────────────

const SEVERITY_LABELS = {
    critical: "Crítico",
    danger: "Urgente",
    warning: "Atención",
    info: "Info",
    success: "Todo bien",
};

const CATEGORY_META = {
    all: { icon: "🔔", label: "Todas" },
    payment: { icon: "💳", label: "Pagos" },
    tokens: { icon: "⚡", label: "Tokens IA" },
    learning: { icon: "🎓", label: "Aprendizaje" },
    admin: { icon: "📨", label: "Del equipo" },
};

// ─────────────────────────────────────────────────────────────────────────────
//  🔧 FIX 1 — HELPERS DE PERSISTENCIA EN localStorage
//  Se usan para readLocal y dismissed para que sobrevivan recargas.
// ─────────────────────────────────────────────────────────────────────────────

const LS_READ_KEY = 'nt_read_alerts';
const LS_DISMISSED_KEY = 'nt_dismissed_alerts';

function loadSet(key) {
    try {
        const raw = localStorage.getItem(key);
        return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch {
        return new Set();
    }
}

function saveSet(key, set) {
    try {
        localStorage.setItem(key, JSON.stringify([...set]));
    } catch {
        // silencioso — no bloquear la UI si localStorage está lleno
    }
}

// ─────────────────────────────────────────────────────────────────────────────
//  DATA HOOK
// ─────────────────────────────────────────────────────────────────────────────

function useStudentAlerts() {
    const [data, setData] = useState(null);
    const [isLoading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const timerRef = useRef(null);

    const fetchAlerts = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch('/api/student/notifications', {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const json = await res.json();
            setData(json);
        } catch (e) {
            setError(e.message);
            setData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    const markRead = useCallback(async (alertIds) => {
        try {
            const token = localStorage.getItem('token');
            await fetch('/api/student/notifications/read', {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ alert_ids: alertIds }),
            });
        } catch (e) {
            console.warn('[Alerts] markRead error:', e);
        }
    }, []);

    // Cada 3 minutos mientras el alumno tenga esto delante; en silencio si se
    // fue a otra pestaña, y una vuelta inmediata al volver (19-09-2026).
    useLatido(async () => { await fetchAlerts(); return false; },
        { base: 3 * 60 * 1000, techo: 10 * 60 * 1000 });

    return { data, isLoading, error, refetch: fetchAlerts, markRead };
}

// ─────────────────────────────────────────────────────────────────────────────
//  HELPERS
// ─────────────────────────────────────────────────────────────────────────────

function timeAgo(isoStr) {
    if (!isoStr) return '—';
    const diff = Date.now() - new Date(isoStr).getTime();
    const secs = Math.floor(diff / 1000);
    const mins = Math.floor(secs / 60);
    const hours = Math.floor(mins / 60);
    const days = Math.floor(hours / 24);

    if (days > 1) return `hace ${days} días`;
    if (days === 1) return 'ayer';
    if (hours > 0) return `hace ${hours}h`;
    if (mins > 0) return `hace ${mins} min`;
    return 'ahora';
}

// ─────────────────────────────────────────────────────────────────────────────
//  SKELETON
// ─────────────────────────────────────────────────────────────────────────────

function Skel({ w = '100%', h = 14, br = 6 }) {
    return (
        <span className="nt-skel" style={{ width: w, height: h, borderRadius: br, display: 'inline-block' }} />
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  🔧 FIX 2 — KPI STRIP
//  Ahora recibe `localUnread` (calculado localmente) en lugar de leer
//  data?.unread_count, que siempre venía del servidor sin reflejar lecturas.
// ─────────────────────────────────────────────────────────────────────────────

function KpiStrip({ data, isLoading, localUnread, localAlerts }) {
    // localAlerts = allAlerts ya filtrado por dismissed (desde el padre)
    const critical = localAlerts.filter(a => a.severity === 'critical').length;
    const admin_ct = localAlerts.filter(a => a.category === 'admin').length;

    const tiles = [
        { icon: '🔔', label: 'Total', val: isLoading ? '—' : localAlerts.length, color: 'var(--nt-info)' },
        { icon: '📩', label: 'Sin leer', val: isLoading ? '—' : localUnread, color: 'var(--accent)' },
        { icon: '🚨', label: 'Críticas', val: isLoading ? '—' : critical, color: 'var(--nt-critical)' },
        { icon: '📨', label: 'Del equipo', val: isLoading ? '—' : admin_ct, color: 'var(--nt-admin)' },
    ];

    return (
        <div className="nt-kpi-grid">
            {tiles.map(t => (
                <div key={t.label} className="nt-card nt-kpi-tile">
                    <span className="nt-kpi-icon">{t.icon}</span>
                    {isLoading
                        ? <Skel w={36} h={28} br={6} />
                        : <span className="nt-kpi-val" style={{ '--stat-color': t.color }}>{t.val}</span>
                    }
                    <span className="nt-kpi-label">{t.label}</span>
                </div>
            ))}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  ALERT CARD INDIVIDUAL
// ─────────────────────────────────────────────────────────────────────────────

function AlertCard({ alert, onDismiss, onCta, onRead }) {
    const [expanded, setExpanded] = useState(false);
    const isRead = alert.is_read;

    function handleClick() {
        setExpanded(v => !v);
        if (!isRead) onRead(alert.id);
    }

    const shortMsg = alert.message?.length > 120 && !expanded
        ? alert.message.slice(0, 120) + '…'
        : alert.message;

    return (
        <div
            className={`nt-alert-card sev-${alert.severity} ${isRead ? 'is-read' : ''}`}
            onClick={handleClick}
            style={{ paddingLeft: '1.4rem' }}
        >
            {!isRead && <div className="nt-unread-dot" />}

            <div className={`nt-alert-icon-wrap sev-${alert.severity}`}>
                <span style={{ fontSize: '1.15rem' }}>{alert.icon}</span>
            </div>

            <div className="nt-alert-body">
                <p className="nt-alert-title">{alert.title}</p>
                <p className="nt-alert-message">{shortMsg}</p>
                <div className="nt-alert-footer">
                    <span className="nt-alert-time">{timeAgo(alert.created_at)}</span>
                    <span className={`nt-sev-chip sev-${alert.severity}`}>
                        {SEVERITY_LABELS[alert.severity] || alert.severity}
                    </span>
                    {alert.message?.length > 120 && (
                        <span style={{ fontSize: '.68rem', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                            {expanded ? '▲ menos' : '▼ más'}
                        </span>
                    )}
                </div>
            </div>

            <div className="nt-alert-actions" onClick={e => e.stopPropagation()}>
                {alert.cta && (
                    <button
                        className="nt-cta-btn"
                        onClick={() => onCta(alert.cta.action)}
                    >
                        {alert.cta.label} →
                    </button>
                )}
                <button
                    className="nt-dismiss-btn"
                    onClick={() => onDismiss(alert.id)}
                    title="Descartar"
                >
                    ✕
                </button>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  EMPTY STATE
// ─────────────────────────────────────────────────────────────────────────────

function EmptyState({ filter }) {
    const isFiltered = filter !== 'all';
    return (
        <div className="nt-card nt-empty">
            <div className="nt-empty-icon">{isFiltered ? '🔍' : '🎉'}</div>
            <h3 className="nt-empty-title">
                {isFiltered
                    ? `Sin alertas en "${CATEGORY_META[filter]?.label}"`
                    : '¡Todo está en orden!'}
            </h3>
            <p className="nt-empty-sub">
                {isFiltered
                    ? 'Prueba seleccionando otra categoría o espera a que el sistema detecte algo.'
                    : 'No tienes alertas pendientes. Sigue así — tu cuenta está al día y tu racha de aprendizaje va genial.'}
            </p>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

export default function StudentAlertsTab({ onSectionChange }) {
    const { data, isLoading, error, refetch, markRead } = useStudentAlerts();

    const [filter, setFilter] = useState('all');

    // 🔧 FIX 1 — Inicializar desde localStorage en lugar de Set vacío
    const [dismissed, setDismissed] = useState(() => loadSet(LS_DISMISSED_KEY));
    const [readLocal, setReadLocal] = useState(() => loadSet(LS_READ_KEY));

    // 🔧 FIX 1 — Persistir en localStorage cada vez que cambian
    useEffect(() => { saveSet(LS_DISMISSED_KEY, dismissed); }, [dismissed]);
    useEffect(() => { saveSet(LS_READ_KEY, readLocal); }, [readLocal]);

    // ── Derived lists ────────────────────────────────────────────────────────
    const allAlerts = (data?.alerts || []).filter(a => !dismissed.has(a.id));
    const filtered = filter === 'all'
        ? allAlerts
        : allAlerts.filter(a => a.category === filter);

    const categoryCounts = {};
    for (const cat of Object.keys(CATEGORY_META)) {
        if (cat === 'all') continue;
        categoryCounts[cat] = allAlerts.filter(a => a.category === cat).length;
    }

    // 🔧 FIX 2 — Este conteo local es el que se pasa a KpiStrip
    const totalUnread = allAlerts.filter(a => !a.is_read && !readLocal.has(a.id)).length;

    // ── Handlers ─────────────────────────────────────────────────────────────
    function handleDismiss(id) {
        setDismissed(prev => new Set([...prev, id]));
    }

    function handleDismissAll() {
        setDismissed(prev => new Set([...prev, ...filtered.map(a => a.id)]));
    }

    function handleMarkAllRead() {
        const ids = allAlerts.filter(a => !a.is_read).map(a => a.id);
        setReadLocal(prev => new Set([...prev, ...ids]));
        if (ids.length) markRead(ids);
    }

    function handleRead(id) {
        if (!readLocal.has(id)) {
            setReadLocal(prev => new Set([...prev, id]));
            markRead([id]);
        }
    }

    function handleCta(action) {
        if (!onSectionChange) return;
        if (action === 'contact') {
            window.open('https://wa.me/', '_blank');
            return;
        }
        if (action && action !== 'dashboard') {
            onSectionChange(action);
        } else {
            onSectionChange('dashboard');
        }
    }

    // ── Loading skeleton ─────────────────────────────────────────────────────
    const SkeletonCard = () => (
        <div className="nt-card" style={{ padding: '1rem 1.1rem', display: 'flex', gap: '.85rem', alignItems: 'flex-start' }}>
            <Skel w={42} h={42} br={13} />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Skel w="55%" h={16} />
                <Skel w="90%" h={12} />
                <Skel w="40%" h={10} />
            </div>
        </div>
    );

    return (
        <div className="nt-root">

            {/* ── Header ───────────────────────────────────────────────────── */}
            <div className="nt-page-header">
                <div>
                    <div className="nt-page-title-row">
                        <h1 className="nt-page-title">Centro de Alertas</h1>
                        {totalUnread > 0 && (
                            <span className="nt-badge">{totalUnread}</span>
                        )}
                    </div>
                    <p className="nt-page-sub">
                        Notificaciones de pago, tokens, aprendizaje y mensajes del equipo NSL
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
                    {totalUnread > 0 && (
                        <button className="nt-btn nt-btn-neu nt-btn-sm" onClick={handleMarkAllRead}>
                            ✓ Marcar leídas
                        </button>
                    )}
                    <button
                        className="nt-btn nt-btn-neu nt-btn-sm"
                        onClick={refetch}
                        disabled={isLoading}
                        title="Actualizar alertas"
                    >
                        {isLoading ? '⏳' : '🔄'} Actualizar
                    </button>
                </div>
            </div>

            {/* ── ¿Y todo esto, en tu teléfono? ────────────────────────────
                Arriba del todo y antes de las cifras: es la diferencia entre
                enterarse cuando pasa y enterarse cuando entre. Y también la
                salida — si un día no las quiere, la apaga aquí mismo. */}
            <AvisosDelAlumno />

            {/* ── KPI Tiles ─────────────────────────────────────────────────── */}
            {/* 🔧 FIX 2 — Pasamos localUnread y localAlerts en lugar de confiar en data del servidor */}
            <KpiStrip
                data={data}
                isLoading={isLoading}
                localUnread={totalUnread}
                localAlerts={allAlerts}
            />

            {/* ── Filtros ───────────────────────────────────────────────────── */}
            <div className="nt-card" style={{ padding: '.9rem 1.1rem' }}>
                <div className="nt-filter-bar">
                    {Object.entries(CATEGORY_META).map(([key, meta]) => {
                        const count = key === 'all'
                            ? allAlerts.length
                            : (categoryCounts[key] || 0);
                        return (
                            <button
                                key={key}
                                className={`nt-filter-pill${filter === key ? ' active' : ''}`}
                                onClick={() => setFilter(key)}
                            >
                                {meta.icon} {meta.label}
                                {count > 0 && (
                                    <span className="nt-filter-count">{count}</span>
                                )}
                            </button>
                        );
                    })}

                    {filtered.length > 0 && (
                        <button
                            className="nt-btn nt-btn-neu nt-btn-sm nt-filter-right"
                            onClick={handleDismissAll}
                            style={{ fontSize: '.68rem', opacity: .75 }}
                        >
                            Descartar {filter === 'all' ? 'todas' : 'estas'} ({filtered.length})
                        </button>
                    )}
                </div>
            </div>

            {/* ── Error state ──────────────────────────────────────────────── */}
            {error && !isLoading && (
                <div className="nt-card" style={{
                    padding: '1.2rem 1.4rem',
                    background: 'rgba(255,94,126,.05)',
                    borderLeft: '4px solid var(--nt-critical)',
                    borderRadius: 'var(--nt-r-inner)',
                    fontSize: '.82rem',
                    color: 'var(--nt-critical)',
                    fontWeight: 600,
                }}>
                    ⚠️ No se pudieron cargar las alertas.{' '}
                    <button
                        onClick={refetch}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontWeight: 700, textDecoration: 'underline' }}
                    >
                        Reintentar
                    </button>
                </div>
            )}

            {/* ── Feed de alertas ──────────────────────────────────────────── */}
            <div className="nt-alert-list">
                {isLoading && Array(4).fill(null).map((_, i) => <SkeletonCard key={i} />)}

                {!isLoading && !error && filtered.length === 0 && (
                    <EmptyState filter={filter} />
                )}

                {!isLoading && !error && filtered.map(alert => (
                    <AlertCard
                        key={alert.id}
                        alert={{
                            ...alert,
                            is_read: alert.is_read || readLocal.has(alert.id),
                        }}
                        onDismiss={handleDismiss}
                        onCta={handleCta}
                        onRead={handleRead}
                    />
                ))}
            </div>

            {/* ── Footer info ──────────────────────────────────────────────── */}
            {!isLoading && data && (
                <p style={{ fontSize: '.7rem', color: 'var(--text-secondary)', textAlign: 'right', margin: 0, opacity: .65 }}>
                    Actualizado {timeAgo(data.generated_at)} · Auto-refresh cada 3 min
                </p>
            )}
        </div>
    );
}